import React from 'react';
import { Link } from 'react-router-dom';

/*
 * The parts of the design language page (v3, the daylight editorial voice),
 * shared by the front door and its Legacy page so both draw from one copy.
 * Page-scoped tokens below; dark mode maps them onto the canonical dark ramp.
 */

const V3_CSS = `
.dl3 {
  --p-paper:      #FAFAF7;
  --p-shade:      #F5F3EC;
  --p-cream:      #FAF6EA;
  --p-hairline:   #E3E1D6;
  --p-hairline-2: #D6D3C6;
  --p-ink:        #1E2A44;
  --p-ink-soft:   #3E4A63;
  --p-label:      #5A6E8C;
  --p-faint:      #667080;
  --p-gold:       #A87400;
  --p-forest:     #3E6B52;
  --p-clay:       #B05A48;
}
.dark .dl3 {
  --p-paper:      var(--bg);
  --p-shade:      var(--surface);
  --p-cream:      #14120A;
  --p-hairline:   var(--rule);
  --p-hairline-2: var(--rule-bright);
  --p-ink:        var(--text-pri);
  --p-ink-soft:   var(--text-sec);
  --p-label:      var(--text-ter);
  --p-faint:      var(--text-dim);
  --p-gold:       var(--mustard);
  --p-forest:     var(--green);
  --p-clay:       var(--coral);
}
.dl3 { background: var(--p-paper); color: var(--p-ink); }
.dl3 .eyebrow { font-family: 'JetBrains Mono', monospace; font-size: 13px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--p-label); font-weight: 500; }
.dl3 .goldi { font-style: italic; color: var(--p-gold); }
.dl3-mark-assemble rect { animation: dl3-settle 0.55s cubic-bezier(0.22,1,0.36,1) both; animation-delay: calc(var(--i) * 90ms); }
@keyframes dl3-settle { from { opacity:0; transform: translateY(-9px); } 60% { opacity:1; transform: translateY(1.5px); } to { opacity:1; transform: translateY(0); } }
.dl3-card { transition: border-color 0.15s, background 0.15s; }
.dl3-card:hover { border-color: var(--p-hairline-2); background: var(--p-paper); }
.dl3-card .go { opacity: 0; transform: translateX(-2px); transition: opacity 0.15s, transform 0.15s; }
.dl3-card:hover .go { opacity: 1; transform: translateX(0); }
@media (prefers-reduced-motion: reduce) {
  .dl3-mark-assemble rect { animation: none; }
  .dl3-card, .dl3-card .go { transition: none; }
}
`;

const MARK_BARS: [string, number, number, number, number][] = [
  ['#bdbec5', 0, 82.27, 142.85, 12.2],
  ['#aaadb9', 15.02, 65.76, 112.82, 12.2],
  ['#9399ad', 28.15, 49.25, 86.54, 12.2],
  ['#6a718b', 39.42, 32.73, 64.02, 12.2],
  ['#4f597a', 48.8, 16.22, 45.25, 12.2],
  ['#424c68', 55.4, 0, 32.34, 11.61],
];

export const Mark: React.FC<{ height: number; assemble?: boolean }> = ({ height, assemble }) => (
  <svg
    viewBox="0 0 142.85 94.47"
    style={{ height, width: 'auto' }}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={assemble ? 'dl3-mark-assemble' : undefined}
  >
    {MARK_BARS.map(([fill, x, y, w, h], i) => (
      <rect key={fill} fill={fill} x={x} y={y} width={w} height={h} style={assemble ? { ['--i' as string]: i } : undefined} />
    ))}
  </svg>
);

/* Deck-style section header: eyebrow · serif heading with one gold-italic phrase. */
export const SectionHead: React.FC<{ num: string; eyebrow: string; children: React.ReactNode; sub?: string }> = ({ num, eyebrow, children, sub }) => (
  <div className="mb-8">
    <div className="eyebrow">{num} &nbsp;·&nbsp; {eyebrow}</div>
    <h2 className="mt-3 font-serif text-[clamp(26px,3.2vw,34px)] font-normal leading-[1.18] tracking-[-0.01em] text-[var(--p-ink)]">
      {children}
    </h2>
    {sub && <p className="mt-3 max-w-[62ch] font-body text-[15px] leading-relaxed text-[var(--p-ink-soft)]">{sub}</p>}
  </div>
);

/* Reference card: paper-shade plate, hairline, optional gold top bar (featured). */
export const Card: React.FC<{
  to: string;
  external?: boolean;
  eye: string;
  title: string;
  desc: string;
  featured?: boolean;
  wide?: boolean;
}> = ({ to, external, eye, title, desc, featured, wide }) => {
  const cls = `dl3-card group flex flex-col gap-2.5 border border-[var(--p-hairline)] bg-[var(--p-shade)] p-6 ${
    featured ? 'border-t-[3px] border-t-[var(--p-gold)]' : ''
  } ${wide ? 'sm:col-span-2' : ''}`;
  const inner = (
    <>
      <span className="eyebrow text-[13px]">{eye}</span>
      <h3 className="font-serif text-[21px] font-normal leading-snug text-[var(--p-ink)]">{title}</h3>
      <p className="font-body text-[14px] leading-relaxed text-[var(--p-ink-soft)]">{desc}</p>
      <div className="go mt-auto pt-2 font-body text-[13px] text-[var(--p-gold)]">Open →</div>
    </>
  );
  return external ? (
    <a href={to} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
  ) : (
    <Link to={to} className={cls}>{inner}</Link>
  );
};

/* Row with a left accent bar — the deck's term/definition motif. */
export const BarRow: React.FC<{ term: string; def: string; bar?: string }> = ({ term, def, bar }) => (
  <div className="flex items-stretch gap-0 border-b border-[var(--p-hairline)] last:border-b-0">
    <span className="w-[3px] shrink-0 self-stretch" style={{ background: bar ?? 'var(--p-label)' }} />
    <div className="flex flex-1 flex-col gap-1 py-4 pl-6 sm:flex-row sm:items-baseline sm:gap-8">
      <span className="w-44 shrink-0 font-serif text-[17px] text-[var(--p-ink)]">{term}</span>
      <span className="font-body text-[14.5px] leading-relaxed text-[var(--p-ink-soft)]">{def}</span>
    </div>
  </div>
);

/* Archive chip — quiet, square, letterspaced. */
const chipCls =
  'inline-flex items-center border border-[var(--p-hairline)] px-3.5 py-2 font-mono text-[13px] uppercase tracking-[0.16em] text-[var(--p-label)] transition-colors hover:border-[var(--p-hairline-2)] hover:text-[var(--p-ink)]';

export const Chip: React.FC<{ to: string; label: string; external?: boolean }> = ({ to, label, external }) =>
  external ? (
    <a href={to} className={chipCls}>{label}</a>
  ) : (
    <Link to={to} className={chipCls}>{label}</Link>
  );

/* The page-scoped tokens and motion. Render once inside a .dl3 wrapper. */
export const DesignLanguageStyles: React.FC = () => <style>{V3_CSS}</style>;
